import API from '@/api';

const GET_DATA = 'GET_DATA';
const GET_DATA_FAILED = 'GET_DATA_FAILED';
const GET_DATA_SUCCESS = 'GET_DATA_SUCCESS';
const GET_IBM_DATA = 'GET_IBM_DATA';
const GET_IBM_DATA_FAILED = 'GET_IBM_DATA_FAILED';
const GET_IBM_DATA_SUCCESS = 'GET_IBM_DATA_SUCCESS';
const SET_CURRENT_CONFIG = 'SET_CURRENT_CONFIG';
const SET_CONFIG_LIST = 'SET_CONFIG_LIST';
const CLEAR_CURRENT_CONFIG = 'CLEAR_CURRENT_CONFIG';
const PUSH_TO_PROD_AI_CONFIG = 'PUSH_TO_PROD_AI_CONFIG';
const PUSH_TO_PROD_AI_CONFIG_FAILED = 'PUSH_TO_PROD_AI_CONFIG_FAILED';
const PUSH_TO_PROD_AI_CONFIG_SUCCESS = 'PUSH_TO_PROD_AI_CONFIG_SUCCESS';

// Enabled the module to be namespaced
const namespaced = true;

const state = {
  loading: false,
  ibmLoading: false,
  configList: [],
  currentConfig: null,
  loadingPushToProdAIConfig: false,
};

const getters = {
  isLoading: (state) => {
    return state.loading;
  },

  configList: (state) => {
    return (
      state.configList?.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      }) || []
    );
  },

  currentConfig: (state) => {
    return state.currentConfig;
  },

  getConfigById: (state) => {
    return (id) => {
      return state.configList.find((config) => {
        return config.id === parseInt(id);
      });
    };
  },
};

const actions = {
  // * Get all AI Commentary configs
  async getAICommentaryList({ commit }) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.getAICommentaryList();
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(SET_CONFIG_LIST, response.data);
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * Get single AI Commentary config by ID
  async getAICommentary({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.getAICommentary(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(SET_CURRENT_CONFIG, response.data);
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * Create new AI Commentary config
  async createAICommentary({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.createAICommentary(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * Update existing AI Commentary config
  async updateAICommentary({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.updateAICommentary(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(SET_CURRENT_CONFIG, response.data);
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * Publish AI Commentary config
  async publishAICommentary({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.publishAICommentary(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(SET_CURRENT_CONFIG, { ...response.data, res_status: response.status });
        commit(GET_DATA_SUCCESS);
        resolve({ ...response.data, res_status: response.status });
      }
    });
  },

  // * Push AI Commentary config to production
  async pushToProdAIConfig({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.pushToProdAIConfig(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * pushToProdAIConfigBulk pushes a single commentary config and version to production
  // * this pushes a single commentary config in an array, one at a time.
  async pushToProdAIConfigBulk({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      console.log(`Calling pushToProdAIConfigBulk API for ${payload.context_type}`);

      response = await API.aiCommentary.pushToProdAIConfig(payload);

      // await new Promise((resolve) => {
      //   return setTimeout(resolve, 500);
      // }); // Simulated network delay
    } catch (error) {
      console.error(`Error calling pushToProdAIConfigBulk API for ${payload.context_type}:`, error);
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS);
        resolve(response);
      }
    });
  },

  // * Delete AI Commentary config
  async deleteAICommentary({ commit }, payload) {
    let response = {};

    commit(GET_DATA);

    try {
      response = await API.aiCommentary.deleteAICommentary(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * Get IBM Models
  async getIBMModels({ commit }, payload) {
    let response = {};

    commit(GET_IBM_DATA);

    try {
      response = await API.aiCommentary.getIBMModels(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_IBM_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_IBM_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  async getAIConfigVersion({ commit }, payload) {
    let response = {};
    commit(GET_DATA);

    try {
      response = await API.aiCommentary.getAIConfigVersion(payload);
    } catch (error) {
      response = error;
    }

    return new Promise((resolve, reject) => {
      if (response.status >= 400) {
        commit(GET_DATA_FAILED);
        reject(response);
      } else {
        commit(GET_DATA_SUCCESS);
        resolve(response.data);
      }
    });
  },

  // * Clear current config (useful when navigating away)
  clearCurrentConfig({ commit }) {
    commit(CLEAR_CURRENT_CONFIG);
  },
};

// @ MUTATIONS ====================================================================
const mutations = {
  [GET_DATA](state) {
    state.loading = true;
  },

  [GET_DATA_FAILED](state) {
    state.loading = false;
  },

  [GET_DATA_SUCCESS](state) {
    state.loading = false;
  },

  [GET_IBM_DATA](state) {
    state.ibmLoading = true;
  },

  [GET_IBM_DATA_FAILED](state) {
    state.ibmLoading = false;
  },

  [GET_IBM_DATA_SUCCESS](state) {
    state.ibmLoading = false;
  },

  [SET_CONFIG_LIST](state, data) {
    state.configList = data;
  },

  [SET_CURRENT_CONFIG](state, data) {
    state.currentConfig = data;
  },

  [CLEAR_CURRENT_CONFIG](state) {
    state.currentConfig = null;
  },

  [PUSH_TO_PROD_AI_CONFIG](state) {
    state.loadingPushToProdAIConfig = true;
  },

  [PUSH_TO_PROD_AI_CONFIG_FAILED](state) {
    state.loadingPushToProdAIConfig = false;
  },

  [PUSH_TO_PROD_AI_CONFIG_SUCCESS](state, data) {
    state.loadingPushToProdAIConfig = false;
  },
};

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
